import React from "react"

import { RecordCommunication } from "./RecordCommunication.tsx"
import { RecordSendActions } from "./RecordSendActions.tsx"
import {
  type CommRecord,
  primaryContactOf,
  useRecordContacts
} from "./record-contacts.ts"

interface PersonLike {
  id?: string
  first_name?: string | null
  last_name?: string | null
  middle_name?: string | null
}

/** The fields a lead or a project shares that Communication reads. */
export interface CommunicationSubject {
  id: string
  lead_name?: string | null
  customer?: PersonLike | null
  temporary_customer?: PersonLike | null
  won_at?: string | null
}

const recordOf = (
  type: CommRecord["type"],
  subject: CommunicationSubject
): CommRecord => {
  const contact = primaryContactOf(subject)
  return {
    type,
    id: subject.id,
    label: subject.lead_name || contact.name
  }
}

/** The record's Communication tab: contact strip, toolbar, messages and queue. */
export const RecordCommunicationTab = ({
  type,
  subject
}: {
  type: CommRecord["type"]
  subject: CommunicationSubject
}) => {
  const record = recordOf(type, subject)
  const { contacts } = useRecordContacts(record, primaryContactOf(subject))
  return (
    <RecordCommunication
      record={record}
      contacts={contacts}
      wonAt={type === "project" ? subject.won_at : null}
    />
  )
}

/** The record header's Send email / Send SMS buttons. */
export const RecordHeaderSendActions = ({
  type,
  subject,
  doNotContact = false,
  showLabels,
  size
}: {
  size?: "middle" | "large"
  type: CommRecord["type"]
  subject: CommunicationSubject
  doNotContact?: boolean
  showLabels?: boolean
}) => {
  const record = recordOf(type, subject)
  const { contacts } = useRecordContacts(record, primaryContactOf(subject))
  return (
    <RecordSendActions
      record={record}
      contacts={contacts}
      doNotContact={doNotContact}
      showLabels={showLabels}
      size={size}
    />
  )
}
